<template>
  <div class="Evaluation-modal-overlay">
    <transition name="modal-expand">
      <div v-if="isVisible" class="Evaluation-modal" @click.stop>
        <div class="modal-content">
          <div class="title-container">
            <font-awesome-icon class="window-close-icon" :icon="['fas', 'window-close']" @click="close" />
            <h2>Assign Instructors to {{ $labels.project }} ID: {{
              this.selectedProjectId
              }}</h2>
          </div>
          <div class="flex-grow-1 d-flex justify-content-center">
            <div v-if="isDisabled" class="expired-box">
              This {{ $labels.project }} has expired. Instructors can no longer be assigned.
            </div>
          </div>
          <div class="container-fluid">
            <vue-good-table :columns="columns" :rows="filteredInstructors"
              :pagination-options="{ enabled: true, perPage: 10 }" :search-options="{ enabled: true }"
              :sort-options="{ enabled: true }" styleClass="vgt-table condensed">
              <template #table-row="props">
                <!-- Assign Checkbox -->
                <template v-if="props.column.field === 'select'">
                  <div @click="toggleCheckbox(props.row.user_id)">
                  <input type="checkbox" v-model="selectedInstructors" :value="props.row.user_id"
                    :disabled="alreadyAssignedInstructors.includes(props.row.user_id) || isDisabled" @click.stop>
                  </div>
                </template>
                <!-- Remove Trash Icon -->
                <span v-else-if="props.column.field === 'remove'">
                  <font-awesome-icon icon="trash-alt" class="delete-icon"
                    v-if="alreadyAssignedInstructors.includes(props.row.user_id)"
                    :class="{ 'disabled-icon': isDisabled }" @click="removeInstructor(props.row.user_id)" />
                </span>
                <!-- Default -->
                <span v-else :title="String(props.formattedRow[props.column.field] ?? '')">
                  {{ props.formattedRow[props.column.field] }}
                </span>
              </template>
            </vue-good-table>
          </div>
          <div class="button-container">
            <button class="ins-close-button" @click="close()">Close</button>
            <button class="ins-save-button" @click="assignSelectedInstructors" :disabled="isDisabled">Assign
              Instructors</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';
import { VueGoodTable } from 'vue-good-table-next';
import 'vue-good-table-next/dist/vue-good-table-next.css';

export default {
  name: 'AssignInstructorsModal',
  components: {
    VueGoodTable,
  },
  props: {
    selectedProjectId: {
      type: Number,
      required: true,
    },
    status: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      instructors: [],
      selectedInstructors: [],
      columns: [
        { label: 'ID', field: 'user_id', sortable: true, thClass: 'vgt-id-column', tdClass: 'vgt-id-column' },
        { label: 'First Name', field: 'first_name', sortable: true, thClass: 'vgt-first-name-column', tdClass: 'vgt-first-name-column' },
        { label: 'Last Name', field: 'last_name', sortable: true, thClass: 'vgt-last-name-column', tdClass: 'vgt-last-name-column' },
        { label: 'Email', field: 'email', sortable: true, thClass: 'vgt-email-column', tdClass: 'vgt-email-column' },
        { label: 'Select Instructor', field: 'select', sortable: false, type: 'html', thClass: 'vgt-actions-column', tdClass: 'vgt-actions-column' },
        { label: 'Remove Instructor', field: 'remove', sortable: false, type: 'html', thClass: 'vgt-actions-column', tdClass: 'vgt-actions-column' },
      ],
      alreadyAssignedInstructors: [],
      isVisible: false,
    };
  },
  computed: {
    filteredInstructors() {
      return this.instructors;
    },
    isDisabled() {
      return this.status === 1;
    },
  },
  methods: {
    close() {
      this.$emit('close');
    },
    toggleCheckbox(userId) {
      if (this.alreadyAssignedInstructors.includes(userId)) return;

      const index = this.selectedInstructors.indexOf(userId);
      if (index > -1) {
        this.selectedInstructors.splice(index, 1);
      } else {
        this.selectedInstructors.push(userId);
      }
    },
    async assignSelectedInstructors() {
      if (this.selectedInstructors.length === 0) {
        return;
      }

      const project_id = this.selectedProjectId;
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const existingInstructors = this.alreadyAssignedInstructors;

      // Filter out Instructors already assigned to the project
      const newInstructors = this.selectedInstructors.filter(
        instructorId => !existingInstructors.includes(instructorId)
      );

      console.log('New instructors to assign:', newInstructors);

      if (newInstructors.length === 0) {
        window.alert(`All selected instructors are already in the ${this.$labels.project}.`);
        this.selectedInstructors = [];
        return;
      }

      try {
        // Step 1: Assign new instructors to the project
        const projectData = {
          user_ids: newInstructors,
          role: 'instructor',
        };

        const addUsersResponse = await axios.post(
          `/api/projects/${project_id}/add-users`,
          projectData,
          { headers }
        );
        console.log('Instructors added to the Project:', addUsersResponse);

        // Step 2: Fetch logbooks for the project
        const logbookResponse = await axios.get(
          `/api/projects/${project_id}/logbooks`,
          { headers }
        );

        const logbooks = logbookResponse.data;
        const logbookIds = logbooks.map(logbook => logbook.logbook_id);

        if (logbookIds.length === 0) {
          // No logbooks to assign, but Instructors were added to the project
          window.alert(`Instructors added successfully to the ${this.$labels.project}.`);
          this.selectedInstructors = [];
          this.$emit('close');
          return;
        }

        // Step 3: Assign instructors to logbooks
        const logbookData = {
          user_ids: newInstructors,
          logbook_ids: logbookIds,
          project_id,
          role: 'instructor'
        };

        const logbookAssignResponse = await axios.post(
          `/api/logbooks/add-users-to-logbook`,
          logbookData,
          { headers }
        );
        console.log('Instructors added to logbooks:', logbookAssignResponse);

        window.alert(`Instructors added successfully to the ${this.$labels.project} and logbooks.`);
        this.selectedInstructors = [];
        this.$emit('close');
      } catch (error) {
        console.error('Error assigning Instructors or fetching logbooks:', error);
        if (error.isOffline === true) {
          this.$emit('close');
          return;
        }
        window.alert('An error occurred while assigning Instructors. Please try again.');
      }
    },
    async removeInstructor(user_id) {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        // You may want to show a confirmation dialog before removing the instructor
        const confirmRemove = confirm(`Are you sure you want to remove this instructor from the ${this.$labels.project}?`);

        if (!confirmRemove) {
          return;
        }

        // Send a DELETE request to your API to remove the instructor from the Project
        const response = await axios.delete(`/api/user-logbook/remove/${user_id}/${this.selectedProjectId}`, { headers });

        if (response.status === 200) {
          window.alert(`Instructor successfully removed from ${this.$labels.project}.`);
          this.loadData()
        } else {
          // Handle errors here
          console.error('An error occurred while removing the instructor:', response);
        }
      } catch (error) {
        console.error('An error occurred while removing the instructor:', error);
        if (error.isOffline === true) {
          this.$emit('close');
          return;
        }
      }
    },
    async loadData() {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        // Fetch the instructors assigned to the project
        const response = await axios.get(`/api/projects/${this.selectedProjectId}/users`, { headers });
        const assignedInstructors = response.data.filter(user => user.pivot.role === 'instructor');
        this.alreadyAssignedInstructors = assignedInstructors.map(instructor => instructor.user_id);

        // Now, you have the assigned instructors in the 'assignedInstructors' variable.
        console.log(assignedInstructors);

        // Fetch all instructors (including those assigned to the project)
        const result = await axios.get('/api/users', { headers });

        if (result.status === 200) {
          // Filter the response data to include only users with the role 'instructor'
          this.instructors = result.data.filter(user => Array.isArray(user.roles) && user.roles.includes('instructor'));

          this.selectedInstructors = [...this.alreadyAssignedInstructors];
        }

      } catch (error) {
        console.error('An error occurred while fetching data:', error);
      }
    }
  },
  async mounted() {
    this.isVisible = true;
    this.loadData();
  },
};
</script>

<style scoped>
.modal-content h2 {
    font-size: 30px;
    margin-left: 20px;
}

.Evaluation-modal-overlay {
  position: fixed;
  top: 0px;
  left: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: top;
  width: 100%;
  height: 100vh;
  z-index: 1;
}

.Evaluation-modal {
  background-color: #ffffff;
  overflow-y: auto;
  /* Enables vertical scrolling if needed */
  max-width: 90vw;
  max-height: 95vh;
  margin-top: 20px;
  min-width: max(65vw, 1100px);
  padding: 10px;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
  position: fixed;
  display: flex;
  flex-direction: column;
  overflow-wrap: break-word;
  padding: 20px;
  z-index: 1002;
}

.button-container {
  display: flex;
  justify-content: space-between;
  /* Aligns items to the right */
  align-items: center;
  margin-top: 0px;
}

.ins-save-button {
  padding: 10px 10px;
  font-size: 16px;
  width: 180px;
  height: 40px;
  margin-top: 0px;
  margin-right: 20px;
  border: 1px solid rgb(0, 96, 134);
  border-radius: 5px;
  background: #2952c4;
  color: white;
  cursor: pointer;
  text-align: center;
  transition: transform 0.2s, box-shadow 0.2s;
}

.ins-close-button {
  background-color: #6c757d;
  border: 1px solid #4d5358;
  margin-top: 0px;
  color: white;
  padding: 5px 20px;
  border-radius: 5px;
  margin-left: 20px;
}

.ins-close-button:hover {
  background-color: #4d5358;
}

.ins-save-button:disabled {
  background-color: rgb(216, 216, 216);
  cursor: not-allowed;
  color: grey
}

.ins-save-button:disabled:hover {
  background-color: rgb(216, 216, 216);
  color: grey
}

.delete-icon {
  font-size: 16px;
  color: #FF0000;
  /* Red color */
  cursor: pointer;
  transition: color 0.2s, transform 0.2s, box-shadow 0.2s;
}

.delete-icon:hover {
  color: #a82222;
  /* Lighter red color on hover */
  cursor: pointer;
  transform: scale(1.1);
  /* Scale the icon on hover */
  /* You can also add a box-shadow or any other styles you prefer */
}

.modal-content {
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
  padding: 15px;
}

.delete-icon.disabled-icon {
  pointer-events: none;
  cursor: not-allowed;
  opacity: 0.5;
  filter: grayscale(100%);
}

.title-container {
  margin-top: 10px;
  margin-bottom: 15px;
}
</style>